import {
    AfterContentInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    OnInit,
    Renderer2,
    Signal,
    signal,
    viewChild,
    ViewContainerRef,
    WritableSignal
} from "@angular/core";
import {NavigationService} from "../../navigation.service";
import {Direction} from '../../../ui-elements/direction';
import {ComparablePageGeometry, PageAlignment, PageComponentContainer, PageGeometry, PageSpec} from "../../page";
import {ThemeModel} from '../../../theme/theme.model';
import {NgClass} from '@angular/common';
import {SwipableDirective} from '../../../ui-elements/swipable.directive';
import {ResponsiveDirective} from '../../../responsive/responsive.directive';
import {ResponsiveService} from '../../../responsive/responsive.service';
import {CssVarDirective} from '../../../utils/css-var.directive';
import {ActivatableDirective} from '../../../ui-elements/activatable.directive';

@Component({
    selector: "woa-page-component-container",
    templateUrl: "./page-component-container.component.html",
    styleUrl: "./page-component-container.component.scss",
    imports: [
        CssVarDirective,
        NgClass,
        ResponsiveDirective,
        SwipableDirective,
        ActivatableDirective
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PageComponentContainerComponent implements PageComponentContainer, OnInit, AfterContentInit {
    public pageSpec: PageSpec = inject(PageSpec);
    public themeModel: ThemeModel = inject(ThemeModel);
    public responsiveService: ResponsiveService = inject(ResponsiveService);
    private navigationService: NavigationService = inject(NavigationService);
    private renderer: Renderer2 = inject(Renderer2);

    PageAlignment = PageAlignment;
    Direction = Direction;

    private readonly viewContainerRef: Signal<ViewContainerRef> = viewChild.required("content", {read: ViewContainerRef});

    public open: WritableSignal<boolean> = signal(false);
    public initialised: WritableSignal<boolean> = signal(false);
    public geometry: WritableSignal<PageGeometry> = signal(undefined);

    public effectiveGeometry: Signal<PageGeometry> = computed(() =>
            Object.assign(Object.assign(new ComparablePageGeometry(), this.pageSpec.geometry), this.geometry() ?? {}),
        {equal: (a: ComparablePageGeometry, b: ComparablePageGeometry) => a.equals(b)});

    public readonly swipeDirection: Signal<Direction> = computed(() =>
        this.effectiveGeometry().disableSwipe ? Direction.NONE
            : this.effectiveGeometry().alignment === PageAlignment.LEFT ? Direction.LEFT
                : this.effectiveGeometry().alignment === PageAlignment.RIGHT ? Direction.RIGHT
                    : this.effectiveGeometry().alignment === PageAlignment.TOP ? Direction.UP
                        : this.effectiveGeometry().alignment === PageAlignment.BOTTOM ? Direction.DOWN
                            : Direction.NONE);

    private openPending: boolean = false;

    ngOnInit(): void {
        this.renderer.addClass(this.pageSpec.componentRef.location.nativeElement, "page-component");
        this.pageSpec.componentRef.changeDetectorRef.detectChanges();
        this.viewContainerRef().insert(this.pageSpec.componentRef.hostView);
    }

    ngAfterContentInit(): void {
        setTimeout(() => {
            this.initialised.set(true);
            if (this.openPending) {
                this.openPageComponent();
            }
        }, 250);
    }

    dismissPage(): void {
        void this.navigationService.dismissPage(this.pageSpec.componentRef.instance);
    }

    public openPageComponent(): void {
        if (!this.initialised()) {
            this.openPending = true;
            return;
        }
        this.openPending = false;
        this.open.set(true);
    }

    public closePageComponent(): void {
        this.open.set(false);
    }
}
